import type { Checkout } from 'core/domains';
import {
  type PaymentPlan,
  type PaymentPlansResponse,
  PAYMENT_PLAN_TYPE,
  PaymentNewPresenter,
} from '../../../@types';
import { formatMoney } from 'core/utilities';

type BankslipOption = PaymentPlansResponse['data']['bankSlip'];
type CreditCardOption = PaymentPlansResponse['data']['creditCard'];
type LinkPayment = PaymentPlansResponse['data']['linkPayment'];
type CentralPayment = PaymentPlansResponse['data']['centralPayment'];
type PixPayment = PaymentPlansResponse['data']['pixPayment'];

type PaymentOptions = BankslipOption | CreditCardOption | LinkPayment | CentralPayment | PixPayment;

function isCreditCard(
  paymentInfo: PaymentOptions['paymentInfo'],
): paymentInfo is CreditCardOption['paymentInfo'] {
  if (paymentInfo) {
    return (paymentInfo as CreditCardOption['paymentInfo'])[0]?.flag !== undefined;
  }

  return false;
}

function formatDescription(paymentCode: number) {
  const codesThatNotSpendCredit = [125, 139, 225, 226];

  if (codesThatNotSpendCredit.includes(paymentCode)) {
    return 'Não consome crédito';
  }

  if (paymentCode === 1 || paymentCode === 50) {
    return 'Ignorar crédito – não consome crédito';
  }

  return 'Consumirá créditos na hora';
}

export function formatPaymentInfo(
  paymentInfo: PaymentOptions['paymentInfo'],
  paymentPlanType: PaymentOptions['paymentPlanType'],
) {
  const commonProps = (props: Pick<PaymentPlan, 'administrateTax' | 'code' | 'paymentModeId'>) => ({
    administrateTax: formatMoney(props.administrateTax),
    administrateTaxNum: props.administrateTax,
    description: formatDescription(props.code),
    paymentCode: props.code.toString(),
    paymentMode: props.paymentModeId,
  });

  if (paymentPlanType === PAYMENT_PLAN_TYPE.PIX) {
    return paymentInfo?.map((payment) => {
      const { administrateTax, code, ...props } = payment as PaymentPlan;
      const paymentModeId = (props as PaymentPlan).paymentModeId;
      return {
        ...props,
        ...commonProps({
          administrateTax,
          code,
          paymentModeId,
        }),
      };
    });
  }

  if (isCreditCard(paymentInfo)) {
    return paymentInfo.map(({ info, ...attributes }) => ({
      ...attributes,
      info: info.map(({ administrateTax, code, paymentModeId, ...item }) => ({
        ...item,
        ...commonProps({ administrateTax, code, paymentModeId }),
      })),
    }));
  }

  return paymentInfo?.map(({ administrateTax, code, paymentModeId, name, ...item }) => {
    const paymentName = () => {
      if ([1, 50].includes(code)) {
        return 'centralPayment';
      }

      return name;
    };

    return {
      ...item,
      ...commonProps({ administrateTax, code, paymentModeId }),
      name: paymentName(),
    };
  });
}

export function paymentFormatPresenter(payment: PaymentNewPresenter) {
  if (payment) {
    const paymentInfo = payment?.paymentInfo.map((payment) => {
      return {
        administrateTax: payment?.administrateTax,
        code: payment?.code,
        description: '',
        installments: payment?.installments,
        isBlocked: false,
        name: payment?.name,
        paymentModeId: payment?.paymentModeId,
      };
    });
    return {
      name: payment?.name,
      description: payment?.description,
      paymentPlanType: payment?.paymentPlanType,
      textQuestion: payment?.textQuestion,
      paymentInfo: formatPaymentInfo(paymentInfo, payment?.paymentPlanType),
    };
  }
  return {} as unknown as Checkout['paymentMethods']['data']['bankSlip'];
}

export function paymentOptionPresenter(paymentMode: PaymentOptions) {
  const { paymentInfo, paymentPlanType, ...attributes } = paymentMode;

  return {
    ...attributes,
    paymentPlanType,
    paymentInfo: formatPaymentInfo(paymentInfo, paymentPlanType),
  };
}
